class BaseAbortControllerService {
    /**
     * Run a navigator.credentials ceremony, cancelling whichever one is already in
     * flight. Browsers allow only one at a time.
     */
    async run(startCeremony) {
        await this.cancelCeremony("Cancelling existing WebAuthn API call for new one");

        const controller = new AbortController();
        this.controller = controller;

        const ceremony = startCeremony(controller.signal);
        // Kept separately so waiting on the settle never rethrows into whichever
        // ceremony is replacing this one.
        this.settled = ceremony.catch(() => {});

        try {
            return await ceremony;
        } finally {
            if (this.controller === controller) {
                this.controller = undefined;
                this.settled = undefined;
            }
        }
    }

    /**
     * Cancel the active ceremony and wait for the browser to release it.
     */
    async cancelCeremony(reason = "Manually cancelling existing WebAuthn API call") {
        if (!this.controller) return;

        const abortError = new Error(reason);
        abortError.name = "AbortError";
        this.controller.abort(abortError);
        this.controller = undefined;

        // The browser frees its single credentials slot only once the aborted call
        // rejects. Starting the next one sooner throws "A request is already pending."
        await this.settled;
        this.settled = undefined;
    }
}

/**
 * A service singleton to help ensure that only a single navigator.credentials ceremony is active at a time.
 *
 */
export const AbortControllerService = new BaseAbortControllerService();
